import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Header, PAGE_CONTAINER, QuoteForm } from "../components/site";
import { SITE } from "../lib/seo-data";

const TITLE = "Netbeheerders in Nederland: wie is jouw netbeheerder? | offerto";
const DESC =
  "Overzicht van alle netbeheerders in Nederland per regio. Ontdek wie jouw netbeheerder is, wat je doet bij een storing en hoe je je aansluiting verzwaart naar 3-fase.";

const netbeheerders = [
  { naam: "Liander", gebied: "Noord-Holland, Gelderland, Flevoland, delen van Friesland" },
  { naam: "Stedin", gebied: "Grote delen van Zuid-Holland, Utrecht en Zeeland" },
  { naam: "Enexis", gebied: "Noord-Brabant, Limburg, Overijssel, Drenthe, Groningen" },
  { naam: "Westland Infra", gebied: "Westland en Midden-Delfland" },
  { naam: "RENDO Netwerken", gebied: "Delen van Drenthe en Overijssel (o.a. Hoogeveen, Steenwijk)" },
  { naam: "Coteq", gebied: "Regio Almelo, Oldenzaal, Goor" },
  { naam: "TenneT", gebied: "Landelijk hoogspanningsnet (niet voor huisaansluitingen)" },
];

const faq = [
  {
    q: "Kan ik zelf mijn netbeheerder kiezen?",
    a: "Nee. Welke netbeheerder je hebt, ligt vast door de plek waar je woont. Verhuis je naar een andere regio, dan krijg je automatisch de netbeheerder die daar actief is. Je energieleverancier kies je wel zelf.",
  },
  {
    q: "Wat kost een 3-fase aansluiting?",
    a: "Voor het verzwaren van 1-fase naar 3x25A rekenen netbeheerders eenmalig ongeveer € 259 tot € 369 (incl. btw, peildatum januari 2026). Daarnaast bouwt een elektricien je groepenkast om, meestal voor € 600 tot € 900. Controleer de actuele tarieven altijd bij je netbeheerder.",
  },
  {
    q: "Wie betaalt de netbeheerkosten?",
    a: "Iedere aansluiting betaalt periodieke netbeheerkosten. Die staan op je energierekening: je leverancier int ze en draagt ze af aan de netbeheerder. Je krijgt dus geen aparte rekening van de netbeheerder, behalve voor eenmalige werkzaamheden zoals een verzwaring.",
  },
  {
    q: "Heb ik 3-fase nodig voor een laadpaal of warmtepomp?",
    a: "Niet altijd. Een laadpaal tot 3,7 kW en veel warmtepompen werken op 1-fase. Wil je sneller laden (11 kW) of heb je meerdere zware apparaten tegelijk, dan is 3-fase vaak verstandig. Een elektricien kan dit voor je doorrekenen.",
    link: true,
  },
];

export const Route = createFileRoute("/netbeheerders")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE}/netbeheerders` },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/netbeheerders` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Netbeheerders", item: `${SITE}/netbeheerders` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: Netbeheerders,
});

const h2 = "mt-12 text-2xl font-extrabold tracking-tight";
const p = "mt-3 text-muted-foreground leading-relaxed";

function Netbeheerders() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className={`${PAGE_CONTAINER} py-12`}>
        <nav aria-label="Kruimelpad" className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link> <span aria-hidden="true">›</span>{" "}
          <span className="text-foreground">Netbeheerders</span>
        </nav>
        <div className="max-w-3xl">
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl">Netbeheerders in Nederland</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Een netbeheerder zorgt ervoor dat stroom en gas veilig bij je huis aankomen. Hij beheert de kabels en leidingen in de straat, je aansluiting tot en met de meter en lost storingen in het net op. Je netbeheerder kies je niet zelf: dat hangt af van waar je woont.
          </p>

          <h2 className={h2}>Overzicht netbeheerders elektriciteit</h2>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-secondary text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-3">Netbeheerder</th><th className="px-4 py-3">Werkgebied</th></tr>
              </thead>
              <tbody>
                {netbeheerders.map((n) => (
                  <tr key={n.naam} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-3 font-semibold">{n.naam}</td>
                    <td className="px-4 py-3 text-muted-foreground">{n.gebied}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Werkgebieden zijn indicatief; je exacte netbeheerder staat op je energierekening of zoek je op via je EAN-code.
          </p>

          <h2 className={h2}>Wie is mijn netbeheerder?</h2>
          <p className={p}>Er zijn drie eenvoudige manieren om dat te achterhalen:</p>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-muted-foreground">
            <li><strong className="text-foreground">Je energierekening:</strong> op de jaarafrekening staat bij de netbeheerkosten welke netbeheerder bij jouw adres hoort.</li>
            <li><strong className="text-foreground">Je EAN-code:</strong> deze 18-cijferige code is het unieke nummer van je aansluiting. Met die code kun je je netbeheerder online opzoeken.</li>
            <li><strong className="text-foreground">De netbeheerder zelf:</strong> op de websites van de netbeheerders vul je je postcode in om te zien of je in hun gebied woont.</li>
          </ul>

          <h2 className={h2}>Verschil tussen netbeheerder en energieleverancier</h2>
          <p className={p}>
            Je energieleverancier verkoopt je stroom en gas; die kies en wissel je zelf. De netbeheerder zorgt voor het transport en de aansluiting, ongeacht bij welke leverancier je zit. Valt de stroom in de hele straat uit, dan meld je dat dus bij de netbeheerder en niet bij je leverancier.
          </p>

          <h2 className={h2}>Storing?</h2>
          <p className={p}>
            Bel bij een storing aan gas of stroom het landelijke storingsnummer{" "}
            <a href="tel:08009009" className="font-semibold text-foreground">0800 - 9009</a>. Controleer eerst je eigen groepenkast: is er een aardlekschakelaar of groep uitgeschakeld en hebben de buren wél stroom, dan zit het probleem waarschijnlijk in je woning. Daarvoor schakel je een elektricien in.
          </p>
          <Link to="/elektricien/$dienst" params={{ dienst: "storing-verhelpen" }} className="mt-3 inline-block font-semibold text-accent hover:underline">
            Storing in huis laten verhelpen →
          </Link>

          <h2 className={h2}>Aansluiting verzwaren naar 3-fase</h2>
          <ol className="mt-4 space-y-3">
            {[
              <>Vraag de verzwaring aan via{" "}<a href="https://www.mijnaansluiting.nl/home" target="_blank" rel="noopener" className="font-semibold text-accent hover:underline">MijnAansluiting.nl ↗</a>.</>,
              <>De netbeheerder plant de werkzaamheden en past je aansluiting en meter aan.</>,
              <>Een elektricien bouwt je groepenkast om naar 3-fase.</>,
            ].map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[#14213D] text-sm font-bold text-white">{i + 1}</span>
                <span className="pt-1 text-muted-foreground">{s}</span>
              </li>
            ))}
          </ol>
          <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-secondary text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-3">Kostenpost</th><th className="px-4 py-3">Indicatie</th></tr>
              </thead>
              <tbody>
                {[
                  ["Verzwaring 1-fase → 3x25A, Enexis", "± € 275"],
                  ["Verzwaring 1-fase → 3x25A, Liander", "± € 369"],
                  ["Verzwaring 1-fase → 3x25A, Stedin", "± € 259"],
                  ["Groepenkast ombouwen door elektricien", "± € 600 – € 900"],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-3">{k}</td>
                    <td className="px-4 py-3 font-bold tabular-nums">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Bedragen incl. btw, peildatum januari 2026; controleer actuele tarieven bij je netbeheerder. Door netcongestie kunnen wachttijden in sommige regio's oplopen.
          </p>
          <Link to="/aanvraag" search={{ klus: 0 }} className="mt-5 inline-block rounded-lg bg-accent px-6 py-3 text-sm font-bold text-accent-foreground transition-colors hover:bg-accent-hover">
            Vergelijk offertes voor je groepenkast
          </Link>

          <h2 className={h2}>Veelgestelde vragen</h2>
          <dl className="mt-4 space-y-5">
            {faq.map((f) => (
              <div key={f.q}>
                <dt className="font-semibold">{f.q}</dt>
                <dd className="mt-1 text-muted-foreground">
                  {f.a}
                  {f.link && (
                    <>
                      {" "}
                      <Link to="/elektricien/$dienst" params={{ dienst: "laadpaal-installeren" }} className="font-semibold text-accent hover:underline">Laadpaal laten installeren →</Link>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-14">
          <QuoteForm />
        </div>
      </main>
      <Footer />
    </div>
  );
}
